import { type FC, type KeyboardEvent, useLayoutEffect, useRef, useState } from 'react';
import { _notNil } from '@/littledash';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import type { SubEditorProps } from '@/components/UI/DataTableV2/SingleCellEditor/SingleCellEditor';
import styles from './TextCellEditor.module.scss';

/**
 * ContentEditable text editor for text/number/metadata-plain columns.
 *
 * Seeds content from the `content` prop (typed character) or the cell's current
 * value, handles Enter/Tab to commit-and-move, Escape to cancel, and blur to
 * commit. Reads the current cell value via `dataTableService.cellData()` and
 * commits via the `onCommit` callback. Guards against the escape/blur race:
 * once Escape calls onCancel, a subsequent blur does not also commit.
 */
export const TextCellEditor: FC<SubEditorProps> = ({ idCoordinate, content, onCommit, onCancel }) => {
  const { dataTableService } = useDataTable();
  const editorRef = useRef<HTMLDivElement>(null);
  const finishedRef = useRef(false);

  const [initialValue] = useState(() => {
    return _notNil(content) && content.length > 0 ? content : (dataTableService.cellData(idCoordinate)?.value ?? '');
  });

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    const selection = window.getSelection();
    const range = document.createRange();
    selection?.removeAllRanges();
    range.selectNodeContents(editor);
    range.collapse(false);
    selection?.addRange(range);
    editor.focus();
  }, []);

  const extractAndNormalizeText = (editor: HTMLDivElement): string | null => {
    const text = (editor.innerText ?? editor.textContent ?? '').trim();
    return text.length === 0 ? null : text;
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const editor = editorRef.current;
    if (!editor || finishedRef.current) {
      return;
    }

    switch (event.key) {
      case 'Escape': {
        event.preventDefault();
        finishedRef.current = true;
        onCancel();
        break;
      }

      case 'Enter': {
        if (!event.ctrlKey) {
          event.preventDefault();
          finishedRef.current = true;
          const value = extractAndNormalizeText(editor);
          onCommit(value, { moveSelection: event.shiftKey ? 'UP' : 'DOWN' });
        }
        break;
      }

      case 'Tab': {
        event.preventDefault();
        finishedRef.current = true;
        const value = extractAndNormalizeText(editor);
        onCommit(value, { moveSelection: event.shiftKey ? 'LEFT' : 'RIGHT' });
        break;
      }
    }
  };

  const handleBlur = (): void => {
    const editor = editorRef.current;
    if (!editor || finishedRef.current) {
      return;
    }

    finishedRef.current = true;
    const value = extractAndNormalizeText(editor);
    onCommit(value);
  };

  return (
    <div
      ref={editorRef}
      className={styles.editor}
      contentEditable
      data-testid="text-cell-editor"
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      suppressContentEditableWarning
    >
      {initialValue}
    </div>
  );
};
